import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, AlertCircle, ImageIcon } from 'lucide-react';
import {
  useActivationEventAssets,
  useUploadActivationEventAsset,
} from '@/hooks/useActivationEvents';
import { ASSET_KINDS, type AssetKind } from '@/types/activations';

interface AssetsTabProps {
  eventId: string;
}

const ASSET_KIND_LABELS: Record<AssetKind, string> = {
  Logo: 'Logo',
  Scenery: 'Scenery',
  ReferenceImage: 'Reference image',
};

export function AssetsTab({ eventId }: AssetsTabProps) {
  const { data: assets, isLoading, isError, error, refetch } = useActivationEventAssets(eventId);
  const { uploadAsset, isUploading } = useUploadActivationEventAsset(eventId);

  const fileRef = useRef<HTMLInputElement>(null);
  const [assetKind, setAssetKind] = useState<AssetKind>('Logo');
  const [label, setLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);

  const handleUpload = async () => {
    if (!file) return;
    await uploadAsset({ file, assetKind, label: label.trim() || undefined });
    setFile(null);
    setLabel('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-6" data-testid="assets-tab">
      <Card>
        <CardHeader>
          <CardTitle>Upload generation input asset</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="asset-kind">Asset kind</Label>
              <Select value={assetKind} onValueChange={(v) => setAssetKind(v as AssetKind)}>
                <SelectTrigger id="asset-kind" data-testid="asset-kind-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {ASSET_KIND_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-label">Label (optional)</Label>
              <Input id="asset-label" data-testid="asset-label" value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="asset-file">File</Label>
              <Input
                id="asset-file"
                ref={fileRef}
                data-testid="asset-file"
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleUpload} disabled={!file || isUploading} data-testid="upload-asset-button">
              <Upload className="mr-2 h-4 w-4" />
              {isUploading ? 'Uploading…' : 'Upload asset'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="assets-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      )}

      {isError && (
        <Alert variant="destructive" data-testid="assets-error">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Failed to load assets</AlertTitle>
          <AlertDescription className="flex items-center gap-3">
            {(error as Error)?.message || 'Something went wrong.'}
            <Button size="sm" variant="outline" onClick={() => refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && (assets?.length ?? 0) === 0 && (
        <Card className="flex flex-col items-center gap-3 p-12 text-center" data-testid="assets-empty">
          <ImageIcon className="h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No assets uploaded yet.</p>
        </Card>
      )}

      {!isLoading && !isError && (assets?.length ?? 0) > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(assets ?? []).map((asset) => (
            <Card key={asset.id} data-testid={`asset-card-${asset.id}`}>
              <div className="flex aspect-video items-center justify-center overflow-hidden rounded-t-lg bg-muted">
                <img
                  src={asset.blobUrl ?? undefined}
                  alt={asset.label ?? asset.assetKind}
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              </div>
              <CardContent className="space-y-1 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{asset.label || ASSET_KIND_LABELS[asset.assetKind]}</span>
                  <Badge variant="secondary">{ASSET_KIND_LABELS[asset.assetKind]}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
